/**
 * Galeria mediów pozycji oferty: zrzuty ekranu i filmy.
 *
 * Trzy decyzje, każda z powodu:
 *   • miniatury 16:9, przewijane poziomo na telefonie (JEDYNE dopuszczone
 *     poziome przewijanie na tej stronie — celowe, z `snap-x`);
 *   • podgląd obrazu to natywny `<dialog>` — focus trap, Esc i warstwa
 *     nad wszystkim przychodzą z przeglądarki, nie z naszego kodu;
 *   • wideo z YouTube/Vimeo ładuje ramkę DOPIERO po kliknięciu. Klient czyta
 *     ofertę anonimowo i bez zgody na cudze ciasteczka; do kliknięcia widzi
 *     naszą miniaturę i ikonę odtwarzania, a żądanie do YouTube nie wychodzi.
 */

import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  adresMiniatury,
  adresPliku,
  mediaDoPokazania,
  rozpoznajWideo,
  type Medium,
} from '../../lib/oferta/karta';
import { BAZA_PROJEKTU } from '../../lib/oferta/srodowisko';

interface Props {
  media: Medium[];
  nazwaPozycji: string;
  /** Host projektu Supabase; domyślnie ze środowiska, w testach podawany wprost. */
  bazaProjektu?: string;
}

export function GaleriaMediow({ media, nazwaPozycji, bazaProjektu = BAZA_PROJEKTU }: Props) {
  const lista = mediaDoPokazania(media, bazaProjektu);
  const [podglad, setPodglad] = useState<number | null>(null);
  const [wideo, setWideo] = useState<number | null>(null);

  if (lista.length === 0) return null;

  const podpis = (m: Medium) => m.podpis?.trim() || `${nazwaPozycji} — zrzut ekranu`;

  return (
    <div>
      {wideo !== null && lista[wideo] && (
        <div className="mb-3">
          <OsadzenieWideo
            medium={lista[wideo]}
            podpis={podpis(lista[wideo])}
            bazaProjektu={bazaProjektu}
            onZamknij={() => setWideo(null)}
          />
        </div>
      )}

      <ul className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 sm:grid sm:grid-cols-3 sm:overflow-visible">
        {lista.map((m, i) => {
          const miniatura = adresMiniatury(m, bazaProjektu);
          return (
            <li
              key={`${m.sciezka ?? m.id_wideo ?? ''}-${i}`}
              className="snap-start shrink-0 w-[160px] sm:w-auto"
            >
              <button
                type="button"
                onClick={() => (m.rodzaj === 'wideo' ? setWideo(i) : setPodglad(i))}
                className="relative block w-full aspect-video rounded-ids-sm overflow-hidden border border-aurora-border bg-aurora-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora-brand"
                aria-label={
                  m.rodzaj === 'wideo' ? `Odtwórz: ${podpis(m)}` : `Powiększ: ${podpis(m)}`
                }
              >
                {miniatura ? (
                  <img
                    src={miniatura}
                    alt={podpis(m)}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="absolute inset-0 grid place-items-center text-xs text-aurora-muted px-2 text-center">
                    {podpis(m)}
                  </span>
                )}
                {m.rodzaj === 'wideo' && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 grid place-items-center bg-black/25"
                  >
                    <span className="w-10 h-10 rounded-full bg-black/60 grid place-items-center text-white">
                      <Play className="w-5 h-5 ml-0.5" />
                    </span>
                  </span>
                )}
              </button>
              {m.podpis?.trim() && (
                <p className="mt-1 text-[11px] text-aurora-subtle line-clamp-2">{m.podpis}</p>
              )}
            </li>
          );
        })}
      </ul>

      {podglad !== null && (
        <Podglad
          elementy={lista}
          indeks={podglad}
          podpis={podpis}
          bazaProjektu={bazaProjektu}
          onZmien={setPodglad}
          onZamknij={() => setPodglad(null)}
        />
      )}
    </div>
  );
}

// ─── Lightbox ───────────────────────────────────────────────────────────────

interface PodgladProps {
  elementy: Medium[];
  indeks: number;
  podpis: (m: Medium) => string;
  bazaProjektu: string;
  onZmien: (i: number) => void;
  onZamknij: () => void;
}

function Podglad({ elementy, indeks, podpis, bazaProjektu, onZmien, onZamknij }: PodgladProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const obrazy = elementy.map((m, i) => ({ m, i })).filter(({ m }) => m.rodzaj === 'obraz');
  const pozycja = obrazy.findIndex(({ i }) => i === indeks);
  const biezacy = obrazy[pozycja]?.m;

  const dalej = useCallback(() => {
    if (obrazy.length < 2) return;
    onZmien(obrazy[(pozycja + 1) % obrazy.length].i);
  }, [obrazy, pozycja, onZmien]);
  const wstecz = useCallback(() => {
    if (obrazy.length < 2) return;
    onZmien(obrazy[(pozycja - 1 + obrazy.length) % obrazy.length].i);
  }, [obrazy, pozycja, onZmien]);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (!d.open && typeof d.showModal === 'function') d.showModal();
    const poprzedni = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = poprzedni;
      if (d.open && typeof d.close === 'function') d.close();
    };
  }, []);

  useEffect(() => {
    const onKlawisz = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') dalej();
      if (e.key === 'ArrowLeft') wstecz();
    };
    window.addEventListener('keydown', onKlawisz);
    return () => window.removeEventListener('keydown', onKlawisz);
  }, [dalej, wstecz]);

  if (!biezacy) return null;
  const src = adresPliku(biezacy.sciezka, bazaProjektu);

  return (
    <dialog
      ref={ref}
      onClose={onZamknij}
      onClick={(e) => {
        // Tap w tło (poza obrazem) zamyka.
        if (e.target === e.currentTarget) onZamknij();
      }}
      onPointerDown={(e) => {
        start.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUp={(e) => {
        if (!start.current) return;
        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;
        start.current = null;
        if (dy > 80 && Math.abs(dx) < 60) onZamknij();
        else if (dx > 40 && Math.abs(dy) < 60) wstecz();
        else if (dx < -40 && Math.abs(dy) < 60) dalej();
      }}
      className="bg-transparent p-0 m-0 max-w-none max-h-none w-full h-full backdrop:bg-aurora-scrim"
      aria-label={podpis(biezacy)}
    >
      <div className="w-full h-full flex flex-col items-center justify-center p-3 sm:p-8 text-white">
        <div className="w-full max-w-5xl flex items-center justify-between mb-2">
          <span className="text-xs mono opacity-80">
            {obrazy.length > 1 ? `${pozycja + 1} / ${obrazy.length}` : ''}
          </span>
          <button
            type="button"
            onClick={onZamknij}
            className="w-11 h-11 grid place-items-center rounded-full bg-black/50 hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Zamknij podgląd"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative w-full max-w-5xl flex items-center justify-center">
          {obrazy.length > 1 && (
            <button
              type="button"
              onClick={wstecz}
              className="hidden sm:grid absolute left-0 w-11 h-11 place-items-center rounded-full bg-black/50 hover:bg-black/70"
              aria-label="Poprzedni obraz"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {src && (
            <img
              src={src}
              alt={podpis(biezacy)}
              className="max-h-[80dvh] max-w-full object-contain rounded-ids-md select-none"
              draggable={false}
            />
          )}
          {obrazy.length > 1 && (
            <button
              type="button"
              onClick={dalej}
              className="hidden sm:grid absolute right-0 w-11 h-11 place-items-center rounded-full bg-black/50 hover:bg-black/70"
              aria-label="Następny obraz"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="mt-3 text-sm text-center opacity-90 max-w-3xl">{podpis(biezacy)}</p>
      </div>
    </dialog>
  );
}

// ─── Wideo ──────────────────────────────────────────────────────────────────

interface WideoProps {
  medium: Medium;
  podpis: string;
  bazaProjektu: string;
  onZamknij: () => void;
}

export function OsadzenieWideo({ medium, podpis, bazaProjektu, onZamknij }: WideoProps) {
  const info = rozpoznajWideo(medium, bazaProjektu);
  const plakat = adresMiniatury(medium, bazaProjektu) ?? undefined;

  return (
    <figure className="rounded-ids-md overflow-hidden border border-aurora-border bg-black">
      <div className="aspect-video relative max-h-[60dvh]">
        {info?.dostawca === 'plik' ? (
          <video
            controls
            autoPlay
            playsInline
            preload="metadata"
            poster={plakat}
            src={info.src}
            className="w-full h-full"
            aria-label={podpis}
          />
        ) : info ? (
          <iframe
            src={info.src}
            title={podpis}
            loading="lazy"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <p className="absolute inset-0 grid place-items-center text-white/80 text-sm px-4 text-center">
            Tego filmu nie da się tu odtworzyć.
          </p>
        )}
      </div>
      <figcaption className="flex items-center justify-between gap-3 px-3 py-2 bg-aurora-surface">
        <span className="text-xs text-aurora-muted line-clamp-1">{podpis}</span>
        <button type="button" onClick={onZamknij} className="btn btn--ghost btn--sm">
          Zamknij film
        </button>
      </figcaption>
    </figure>
  );
}
